/**
 * Form Error Type Guards
 *
 * Type-safe utilities for handling Mantine form errors.
 * Prevents unsafe type casting and provides proper runtime validation.
 */

/**
 * Type guard to check if a form error is a string
 *
 * @param error - The error value from form.errors
 * @returns true if error is a string, false otherwise
 *
 * @example
 * ```tsx
 * <TextInput
 *   error={isStringError(form.errors.title) ? form.errors.title : undefined}
 * />
 * ```
 */
export function isStringError(error: unknown): error is string {
  return typeof error === 'string';
}

/**
 * Safely get a string error from form errors
 *
 * @param error - The error value from form.errors
 * @returns The error string if it's a string, undefined otherwise
 *
 * @example
 * ```tsx
 * <TextInput error={getStringError(form.errors.title)} />
 * ```
 */
export function getStringError(error: unknown): string | undefined {
  return isStringError(error) ? error : undefined;
}
